import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../fmeda/services/api.service';
import { ApiService as CoreApiService } from 'src/app/api.service';
import { StoreService } from '../fmeda/services/store.service';
import { Project } from '../fmeda/models';
import { OrganisationService } from 'src/app/organisation.service';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';

@Component({
    selector: 'app-projects',
    standalone: true,
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgbTooltipModule, MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule, MatPaginatorModule]
})
export class ProjectsComponent implements OnInit {
    projects: Project[] = [];
    hasApiError = false;
    apiError = '';
    inContextOrgId = '';
    showCreateForm = false;
    newProjectName = '';
    newProjectId = '';

    dataSource = new MatTableDataSource<Project>(this.projects);
    displayedColumns: string[] = ['id', 'name', 'role', 'owner', 'actions'];

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        public auth: AuthService,
        private api: ApiService,
        private coreApi: CoreApiService,
        private store: StoreService,
        private orgService: OrganisationService,
        private route: ActivatedRoute,
        private router: Router,
    ) { }

    ngOnInit(): void {
        this.orgService.currentOrg$.subscribe((org) => {
            this.inContextOrgId = (org && org.id) ? org.id : '';
        });

        this.auth.isAuthenticated$.subscribe((res) => {
            if (res) {
                this.getProjects();
            }
        });

        this.route.queryParamMap.subscribe((params) => {
            const create = params.get('create');
            if (create === '1' && !this.showCreateForm) {
                this.toggleCreateForm();
            }
        });
    }

    getProjects() {
        this.api.getProjects().subscribe({
            next: (res: any) => {
                this.hasApiError = false;
                this.projects = res.data || [];
                this.dataSource = new MatTableDataSource<Project>(this.projects);
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            },
            error: (error: any) => {
                this.hasApiError = true;
                this.apiError = error.message;
            },
        });
    }

    openProject(project: Project) {
        this.store.setCurrentProject({ id: project.id, name: project.name });
        this.router.navigateByUrl('/fmeda-workspace');
    }

    toggleCreateForm() {
        this.showCreateForm = !this.showCreateForm;
        if (!this.showCreateForm) {
            this.newProjectName = '';
            this.newProjectId = '';
            return;
        }
        // Pre-populate the name field with a generated suggestion.
        this.coreApi.getGeneratedName$().subscribe({
            next: (res) => {
                if (res && res.name && !this.newProjectName.trim()) {
                    this.newProjectName = res.name;
                }
            },
            error: () => {
                // Non-fatal: leave the field blank if name generation fails.
            },
        });
    }

    createProject() {
        const name = this.newProjectName.trim();
        if (!name) {
            return;
        }

        const payload: { name: string; id?: string } = { name };
        const customId = this.newProjectId.trim();
        if (customId) {
            payload.id = customId;
        }

        this.api.createProject(payload, this.inContextOrgId).subscribe({
            next: (res: any) => {
                this.hasApiError = false;
                this.newProjectName = '';
                this.newProjectId = '';
                this.showCreateForm = false;

                const createdProject = res?.data;
                if (createdProject?.id && createdProject?.name) {
                    this.openProject(createdProject);
                    return;
                }

                this.getProjects();
            },
            error: (error: any) => {
                this.hasApiError = true;
                this.apiError = error.error?.message || error.message;
            },
        });
    }

    deleteProject(id: string) {
        const doDelete = confirm('This will permanently delete the project and all associated data. Are you sure?');
        if (!doDelete) {
            return;
        }

        this.api.deleteProject(id).subscribe({
            next: () => {
                this.hasApiError = false;
                this.getProjects();
            },
            error: (error: any) => {
                this.hasApiError = true;
                this.apiError = error.error?.message || error.message;
            },
        });
    }

    ownerLabel(element: Project): string {
        if (element.owner_type === 'ORGANISATION') {
            return element.owner_org_id || 'Organisation';
        }
        return 'Me';
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }
}
